// ** React Import
import { useReactOidc } from '@axa-fr/react-oidc-context';
import { useContext, useEffect, useState } from 'react';
import { Camera, Info } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import QrReader from 'react-qr-reader';
import { useParams } from 'react-router-dom';
import { Button, Card, CardBody, Form, FormGroup, Input, Label, Tooltip } from 'reactstrap';

// ** Custom Components
import Loading from '@views/common/Loading';
import Notification from '@views/common/Notification';

// ** Utils

// ** Context
import { IntlContext } from '@src/utility/context/Internationalization';

// ** Third Party Components
// import { findDepartmentsByOrganizationId } from "@services/DepartmentService"
import { inviteMemberIframe } from '@services/MemberService';

const InviteMember = () => {
    const { oidcUser, login, logout } = useReactOidc();
    const intlContext = useContext(IntlContext);
    const intl = useIntl();

    useEffect(() => {
        login();
    }, []);

    useEffect(() => {
        if (oidcUser) {
            login();
        } else {
            setTimeout(() => {
                logout()
                login()
            }, 1000)
        }
    }, [oidcUser]);

    const headers = {
        Authorization: `Bearer ${oidcUser && oidcUser.access_token}`,
        'Accept-Language': intlContext.locale,
    };

    const { id, appId, messageId, lang } = useParams();

    const [admin, setAdmin] = useState(false);
    const [owner, setOwner] = useState(false);
    const [manager, setManager] = useState({ value: '', label: `${intl.formatMessage({ id: 'member' })}` });
    const [memberDepartments, setMemberDepartments] = useState([]);
    // const [departments, setDepartments] = useState([])
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [emailOrLinkCode, setEmailOrLinkCode] = useState('');
    const [showCamera, setShowCamera] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm();

    useEffect(() => {
        intlContext.switchLanguage(lang ?? 'vi');
    }, [intlContext, lang]);

    const roles = [
        { value: '', label: `${intl.formatMessage({ id: 'member' })}` },
        { value: '1', label: `${intl.formatMessage({ id: 'owner' })}` },
        { value: '2', label: `${intl.formatMessage({ id: 'admin' })}` },
    ];

    const handleChangeRole = (value) => {
        setManager(value);
    };

    useEffect(() => {
        if (manager?.value === '') {
            setOwner(false);
            setAdmin(false);
        }
        if (manager?.value === '1') {
            setOwner(true);
            setAdmin(false);
        }
        if (manager?.value === '2') {
            setAdmin(true);
            setOwner(false);
        }
    }, [manager]);

    const onSubmit = () => {
        setLoading(true);
        inviteMemberIframe(headers, id, appId, messageId, {
            linkCodeOrEmail: emailOrLinkCode,
            admin: owner,
            manager: admin,
            departments: memberDepartments?.map((item) => ({ id: item.value })),
        })
            .then((res) => {
                setSuccess(res?.data);
                window.parent.postMessage('success-invite', '*');
                //
                setMemberDepartments([]);
                setEmailOrLinkCode('');
            })
            .catch((err) => {
                setError(err);
            })
            .finally(() => {
                setSuccess(null);
                setError(null);
                setLoading(false);
                // setMemberDepartments([])
                // setEmailOrLinkCode("")
            });
    };

    const handleScan = (data) => {
        if (data) {
            setEmailOrLinkCode(data);
            setValue('emailOrLinkCode', data);
            setTimeout(() => {
                setShowCamera(false);
            }, 500);
        }
    };

    const handleError = (err) => {
        console.error(err);
    };

    const handleScanQr = () => {
        if (showCamera) {
            setShowCamera(false);
        } else {
            setShowCamera(true);
        }
    };

    const [tooltipEmailOrLinkCode, setTooltipEmailOrLinkCode] = useState(false);
    const toggleTooltip = () => {
        setTooltipEmailOrLinkCode(!tooltipEmailOrLinkCode);
    };

    return (
        <Card>
            {loading && <Loading />}
            {error && <Notification data={{ error }} />}
            {success && <Notification data={{ success }} toastId={'success-invite'} />}
            <CardBody>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup>
                        <Label
                            className="label"
                            for="emailOrLinkCode"
                            onMouseEnter={toggleTooltip}
                            onMouseLeave={toggleTooltip}
                        >
                            <span id="label-email">{intl.formatMessage({ id: 'emailOrLinkCode' })}</span>
                            <span className="text-danger"> * </span>
                            <Info id="icon-info-email" size={15} style={{ color: 'deepskyblue', marginBottom: 5 }} />
                            <Tooltip target="label-email" placement="left" isOpen={tooltipEmailOrLinkCode}>
                                {intl.formatMessage({ id: 'tooltipEmailOrLinkCode' })}
                            </Tooltip>
                        </Label>
                        <div className="d-flex justify-content-left align-items-center">
                            <Input
                                type="text"
                                name="emailOrLinkCode"
                                id="emailOrLinkCode"
                                placeholder={intl.formatMessage({ id: 'placeholderEmailOrLinkCode' })}
                                value={emailOrLinkCode}
                                onChange={(e) => setEmailOrLinkCode(e.target.value)}
                                innerRef={register({
                                    required: `${intl.formatMessage({ id: 'emailOrLinkCodeRequired' })}`,
                                })}
                                invalid={errors.emailOrLinkCode && true}
                            />
                            {window.innerWidth < 500 ? (
                                <Button color="flat" onClick={() => handleScanQr()}>
                                    <Camera size={18} alt="scanQR" />
                                </Button>
                            ) : (
                                ''
                            )}
                        </div>
                        {errors.emailOrLinkCode?.message && (
                            <p style={{ color: 'red' }}>{errors.emailOrLinkCode?.message}</p>
                        )}
                    </FormGroup>
                    {showCamera ? (
                        <FormGroup>
                            <div className="d-flex justify-content-center align-items-center">
                                <QrReader
                                    facingMode="environment"
                                    delay={500}
                                    onError={handleError}
                                    onScan={handleScan}
                                    style={{ width: '75%' }}
                                />
                            </div>
                        </FormGroup>
                    ) : (
                        ''
                    )}
                    <div className="text-right ">
                        <Button type="submit" color="primary">
                            {intl.formatMessage({ id: 'invite' })}
                        </Button>
                    </div>
                </Form>
            </CardBody>
        </Card>
    );
};

export default InviteMember;
